import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Cake,
  HeartHandshake,
  Baby,
  Users,
  Sparkles,
  Crown,
  Calendar,
  Printer,
  Filter,
  Plus,
  Trash2,
  Flag,
  Pencil,
  X,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MONTHS = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

const TYPE_ICON = {
  aniv_irmao: Cake,
  aniv_esposa: HeartHandshake,
  aniv_filho: Baby,
  aniv_neto: Baby,
  casamento: HeartHandshake,
  iniciacao: Sparkles,
  elevacao: Sparkles,
  exaltacao: Sparkles,
  grau_filosofico: Crown,
  custom: Flag,
};

const TYPE_STYLE = {
  aniv_irmao: "bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]/40",
  aniv_esposa: "bg-rose-500/15 text-rose-300 border-rose-500/40",
  aniv_filho: "bg-cyan-500/15 text-cyan-300 border-cyan-500/40",
  aniv_neto: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  casamento: "bg-pink-500/15 text-pink-300 border-pink-500/40",
  iniciacao: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  elevacao: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  exaltacao: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  grau_filosofico: "bg-purple-500/15 text-purple-300 border-purple-500/40",
  custom: "bg-amber-400/10 text-amber-200 border-amber-400/40",
};

const CustomEventModal = ({ isOpen, onClose, onSubmit, initialData = null }) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [category, setCategory] = useState("Data Comemorativa");
  const [recurring, setRecurring] = useState(true);
  const [color, setColor] = useState("#F59E0B");
  const isEdit = !!(initialData && initialData.id);

  useEffect(() => {
    if (!isOpen) return;
    setTitle(initialData?.title || "");
    setDescription(initialData?.description || "");
    setEventDate(initialData?.event_date || "");
    setCategory(initialData?.category || "Data Comemorativa");
    setRecurring(initialData?.recurring !== false);
    setColor(initialData?.color || "#F59E0B");
  }, [isOpen, initialData]);

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!title.trim() || !eventDate) {
      toast.error("Informe título e data.");
      return;
    }
    onSubmit({
      id: initialData?.id || null,
      title: title.trim(),
      description: description.trim(),
      event_date: eventDate,
      category: category.trim() || "Data Comemorativa",
      recurring,
      color,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
      <div className="w-full max-w-md bg-[#131C2E] border border-[#D4AF37]/40 rounded-2xl shadow-[0_0_50px_rgba(212,175,55,0.25)] overflow-hidden flex flex-col" data-testid="modal-custom-event">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800 bg-[#080C14]">
          <div className="flex items-center space-x-2">
            <Flag className="w-5 h-5 text-[#D4AF37]" />
            <h3 className="font-serif font-bold text-lg text-white">
              {isEdit ? "Editar Data Comemorativa" : "Nova Data Comemorativa"}
            </h3>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4 text-xs text-slate-300">
          <div>
            <label className="block mb-1 font-semibold">Título *</label>
            <input
              data-testid="input-event-title"
              type="text"
              required
              placeholder="Ex: Dia da Bandeira, Aniversário da Loja..."
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full bg-[#080C14] text-white p-2.5 rounded-xl border border-slate-700 text-xs"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block mb-1 font-semibold">Data *</label>
              <input
                data-testid="input-event-date"
                type="date"
                required
                value={eventDate}
                onChange={(e) => setEventDate(e.target.value)}
                className="w-full bg-[#080C14] text-white p-2.5 rounded-xl border border-slate-700 text-xs"
              />
            </div>
            <div>
              <label className="block mb-1 font-semibold">Categoria</label>
              <select
                data-testid="input-event-category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full bg-[#080C14] text-white p-2.5 rounded-xl border border-slate-700 text-xs"
              >
                <option>Data Comemorativa</option>
                <option>Cívica</option>
                <option>Aniversário da Loja</option>
                <option>Efeméride Maçônica</option>
                <option>Outro</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block mb-1 font-semibold">Descrição</label>
            <textarea
              data-testid="input-event-description"
              rows="2"
              placeholder="Contexto / origem da data (opcional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full bg-[#080C14] text-white p-2.5 rounded-xl border border-slate-700 text-xs"
            />
          </div>
          <label className="flex items-center gap-2 text-slate-300 cursor-pointer">
            <input
              data-testid="input-event-recurring"
              type="checkbox"
              checked={recurring}
              onChange={(e) => setRecurring(e.target.checked)}
              className="w-4 h-4"
            />
            <span>Repete todo ano no mesmo dia (aniversário perpétuo)</span>
          </label>
          <div className="pt-4 border-t border-slate-800 flex justify-end space-x-3">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-xl bg-slate-800 text-slate-300 hover:text-white font-semibold text-xs">Cancelar</button>
            <button data-testid="btn-submit-custom-event" type="submit" className="bg-[#D4AF37] hover:bg-[#C5A028] text-slate-950 font-bold px-6 py-2 rounded-xl text-xs uppercase shadow-[0_0_15px_rgba(212,175,55,0.3)]">
              {isEdit ? "Salvar" : "Cadastrar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export const CommemorativeDatesView = () => {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [startDay, setStartDay] = useState(1);
  const [endDay, setEndDay] = useState(31);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/commemorative-dates`, {
        params: { month, start_day: startDay, end_day: endDay },
      });
      setData(res.data);
    } catch (e) {
      toast.error("Erro ao carregar datas comemorativas.");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCustom = async (payload) => {
    try {
      if (payload.id) {
        await axios.patch(`${API}/custom-events/${payload.id}`, {
          title: payload.title, description: payload.description, event_date: payload.event_date,
          category: payload.category, recurring: payload.recurring, color: payload.color,
        });
        toast.success("Data comemorativa atualizada!");
      } else {
        await axios.post(`${API}/custom-events`, payload);
        toast.success("Data comemorativa cadastrada!");
      }
      setModalOpen(false); setEditingEvent(null);
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar.");
    }
  };

  const handleDeleteCustom = async (evId) => {
    if (!window.confirm("Excluir esta data comemorativa?")) return;
    try {
      await axios.delete(`${API}/custom-events/${evId}`);
      toast.success("Excluída.");
      fetchData();
    } catch (err) {
      toast.error("Erro ao excluir.");
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, startDay, endDay]);

  const groupedByDay = useMemo(() => {
    if (!data?.events) return {};
    return data.events.reduce((acc, e) => {
      acc[e.day] = acc[e.day] || [];
      acc[e.day].push(e);
      return acc;
    }, {});
  }, [data]);

  return (
    <div className="space-y-6 print:bg-white print:text-black" id="commemorative-dates-print-root" data-testid="commemorative-dates-view">
      <style>{`
        @media print {
          @page { size: A4; margin: 12mm; }
          html, body { background: #fff !important; color: #000 !important; }
          body * { visibility: hidden !important; }
          #commemorative-dates-print-root, #commemorative-dates-print-root * { visibility: visible !important; }
          #commemorative-dates-print-root { position: absolute; left: 0; top: 0; width: 100%; padding: 0 !important; background: #fff !important; color: #000 !important; }
          #commemorative-dates-print-root .luxury-card { background: #fff !important; border: 1px solid #ddd !important; box-shadow: none !important; color: #000 !important; }
          #commemorative-dates-print-root .text-white, #commemorative-dates-print-root .text-slate-200, #commemorative-dates-print-root .text-slate-300, #commemorative-dates-print-root .text-slate-400 { color: #000 !important; }
          #commemorative-dates-print-root button, #commemorative-dates-print-root .print\\:hidden { display: none !important; }
        }
      `}</style>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 print:hidden">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#D4AF37] font-bold mb-1">
            <Calendar className="w-4 h-4" />
            <span>Chancelaria • Efemérides</span>
          </div>
          <h2 className="text-2xl font-serif font-bold text-white">Datas Comemorativas</h2>
          <p className="text-xs text-slate-400 mt-1">
            Aniversários dos Irmãos e famílias, datas ritualísticas e graus filosóficos —
            filtre por mês e faixa de dias para organizar homenagens.
          </p>
        </div>
        <div className="flex items-center gap-2 print:hidden">
          <button
            data-testid="btn-open-custom-event"
            onClick={() => { setEditingEvent(null); setModalOpen(true); }}
            className="flex items-center gap-2 bg-[#131C2E] hover:bg-[#1E293B] border border-slate-700 text-slate-200 font-bold px-4 py-2.5 rounded-xl text-xs uppercase tracking-wider transition-all"
          >
            <Plus className="w-4 h-4 text-[#D4AF37]" />
            Nova Data
          </button>
          <button
            data-testid="commemorative-btn-print"
            onClick={() => window.print()}
            className="flex items-center gap-2 bg-[#D4AF37] hover:bg-[#C5A028] text-slate-950 font-bold px-4 py-2.5 rounded-xl text-xs uppercase tracking-wider transition-all shadow-[0_0_15px_rgba(212,175,55,0.25)]"
          >
            <Printer className="w-4 h-4" />
            Imprimir / PDF
          </button>
        </div>
      </div>

      <div className="luxury-card p-5 print:hidden">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-3">
          <Filter className="w-3 h-3" />
          Filtros
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="text-[10px] uppercase text-slate-400 tracking-widest font-bold">Mês</label>
            <select
              data-testid="commemorative-month"
              value={month}
              onChange={(e) => setMonth(parseInt(e.target.value))}
              className="mt-1 w-full bg-[#131C2E] border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-[#D4AF37] outline-none"
            >
              {MONTHS.map((mn, i) => (
                <option key={i} value={i + 1}>{mn}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase text-slate-400 tracking-widest font-bold">Dia inicial</label>
            <input
              data-testid="commemorative-start-day"
              type="number"
              min="1"
              max="31"
              value={startDay}
              onChange={(e) => setStartDay(parseInt(e.target.value) || 1)}
              className="mt-1 w-full bg-[#131C2E] border border-slate-700 rounded-lg px-3 py-2 text-sm text-white font-mono focus:border-[#D4AF37] outline-none"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase text-slate-400 tracking-widest font-bold">Dia final</label>
            <input
              data-testid="commemorative-end-day"
              type="number"
              min="1"
              max="31"
              value={endDay}
              onChange={(e) => setEndDay(parseInt(e.target.value) || 31)}
              className="mt-1 w-full bg-[#131C2E] border border-slate-700 rounded-lg px-3 py-2 text-sm text-white font-mono focus:border-[#D4AF37] outline-none"
            />
          </div>
          <div className="flex items-end">
            <div className="text-[10px] uppercase text-slate-400 tracking-widest font-bold">
              <div className="text-2xl font-serif text-[#D4AF37]" data-testid="commemorative-total">{data?.total ?? 0}</div>
              <div>Eventos no período</div>
            </div>
          </div>
        </div>
      </div>

      <div className="luxury-card p-6">
        <h3 className="text-lg font-serif font-bold text-white mb-4">
          {MONTHS[month - 1]} — dias {startDay} a {endDay}
        </h3>

        {loading && <div className="text-center text-slate-400 text-xs py-6">Carregando…</div>}

        {!loading && (!data?.events || data.events.length === 0) && (
          <div className="text-center text-slate-500 text-xs italic py-8">
            Nenhuma data comemorativa cadastrada neste período. Cadastre datas de aniversário, casamento e graus no perfil de cada Irmão.
          </div>
        )}

        <div className="space-y-4">
          {Object.entries(groupedByDay).sort((a, b) => Number(a[0]) - Number(b[0])).map(([day, events]) => (
            <div key={day} className="border-l-4 border-l-[#D4AF37]/30 pl-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/15 text-[#D4AF37] font-serif font-bold text-2xl flex items-center justify-center">
                  {String(day).padStart(2, "0")}
                </div>
                <div className="text-xs uppercase tracking-widest text-slate-400 font-bold">
                  {events.length} evento{events.length > 1 ? "s" : ""}
                </div>
              </div>
              <div className="space-y-2">
                {events.map((e, i) => {
                  const Icon = TYPE_ICON[e.type] || Cake;
                  const style = TYPE_STYLE[e.type] || "bg-slate-800 text-slate-300 border-slate-700";
                  const isCustom = e.is_custom;
                  return (
                    <div key={i} className={`flex items-center justify-between gap-3 rounded-xl border px-4 py-3 ${style}`}>
                      <div className="flex items-center gap-3 min-w-0">
                        <Icon className="w-4 h-4 flex-shrink-0" />
                        <div className="min-w-0">
                          <div className="text-sm font-semibold truncate text-white">
                            {isCustom ? e.title : e.person_name}
                            {!isCustom && e.years_completed !== null && e.years_completed !== undefined && (
                              <span className="text-xs text-slate-300 ml-2">({e.years_completed} anos)</span>
                            )}
                            {isCustom && e.years > 0 && (
                              <span className="text-xs text-slate-300 ml-2">({e.years} anos)</span>
                            )}
                          </div>
                          <div className="text-[10px] uppercase tracking-widest opacity-70">
                            {e.type_label}
                            {!isCustom && e.member_name && <> • Ir. {e.member_name}</>}
                            {isCustom && e.description && <> • {e.description}</>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {!isCustom && e.member_email && (
                          <div className="text-[10px] font-mono text-slate-400 truncate max-w-[200px]">
                            {e.member_email}
                          </div>
                        )}
                        {isCustom && (
                          <div className="flex items-center gap-1 print:hidden">
                            <button
                              data-testid={`btn-edit-custom-${e.id}`}
                              onClick={() => { setEditingEvent(e); setModalOpen(true); }}
                              className="p-1 rounded text-slate-400 hover:text-[#D4AF37]"
                              title="Editar"
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button
                              data-testid={`btn-delete-custom-${e.id}`}
                              onClick={() => handleDeleteCustom(e.id)}
                              className="p-1 rounded text-slate-400 hover:text-rose-400"
                              title="Excluir"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      <CustomEventModal
        isOpen={modalOpen}
        onClose={() => { setModalOpen(false); setEditingEvent(null); }}
        onSubmit={handleSaveCustom}
        initialData={editingEvent}
      />
    </div>
  );
};
